// src/components/FileShareButton.tsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { saveAs } from "file-saver";
import copy from "copy-to-clipboard";
import { Ripple } from "primereact/ripple";

type FileShareButtonProps = {
    fileUrl: string;
    fileName: string;
    className?: string;
    disabled?: boolean;
    fetchInit?: RequestInit;
    maxBytes?: number;
    shareTitle?: string;
    shareText?: string;
    onShared?: () => void;
    onFallbackUsed?: (type: "download" | "copy-link" | "download+copy") => void;
    onError?: (err: unknown) => void;
    variant?: "default" | "custom" | "icon";
};

function ensureExtension(fileName: string, fallbackExt: string) {
    const trimmed = (fileName || "").trim();
    if (!trimmed) return `document.${fallbackExt}`;
    if (/\.[a-z0-9]{2,6}$/i.test(trimmed)) return trimmed;
    return `${trimmed}.${fallbackExt}`;
}

async function fetchBlob(url: string, init?: RequestInit): Promise<Blob> {
    const res = await fetch(url, { method: "GET", ...init });
    if (!res.ok) throw new Error(`Failed to fetch file (${res.status})`);
    return await res.blob();
}

function canShareFile(file: File) {
    const navAny = navigator as any;
    if (typeof navAny?.share !== "function") return false;
    if (typeof navAny?.canShare !== "function") return false;
    try {
        return !!navAny.canShare({ files: [file] });
    } catch {
        return false;
    }
}

export const FileShareButton: React.FC<FileShareButtonProps> = ({
    fileUrl,
    fileName,
    className,
    disabled,
    fetchInit,
    maxBytes,
    shareTitle,
    shareText,
    onShared,
    onFallbackUsed,
    onError,
    variant = "default",
}) => {
    const [blob, setBlob] = useState<Blob | null>(null);
    const [loading, setLoading] = useState(false);
    const [pressed, setPressed] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                setLoading(true);
                const b = await fetchBlob(fileUrl, fetchInit);

                if (maxBytes && b.size > maxBytes) {
                    throw new Error(
                        `File too large (${Math.round(b.size / 1024 / 1024)}MB). Max is ${Math.round(
                            maxBytes / 1024 / 1024
                        )}MB`
                    );
                }

                if (!cancelled) setBlob(b);
            } catch (e) {
                if (!cancelled) setBlob(null);
                onError?.(e);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [fileUrl, fetchInit, maxBytes, onError]);

    const inferredName = useMemo(() => {
        const lowerUrl = fileUrl.toLowerCase();
        const defaultExt = lowerUrl.includes(".pdf") ? "pdf" : "bin";
        return ensureExtension(fileName, defaultExt);
    }, [fileUrl, fileName]);

    const buildFile = useCallback((): File => {
        if (!blob) throw new Error("File not ready yet");
        const type =
            blob.type ||
            (inferredName.toLowerCase().endsWith(".pdf") ? "application/pdf" : "application/octet-stream");
        return new File([blob], inferredName, { type });
    }, [blob, inferredName]);

    const fallbackDownloadAndCopy = useCallback(() => {
        try {
            if (blob) {
                const f = buildFile();
                saveAs(f, inferredName);
                copy(fileUrl);
                onFallbackUsed?.("download+copy");
                onShared?.();
                return;
            }
        } catch { }
        copy(fileUrl);
        onFallbackUsed?.("copy-link");
        onShared?.();
    }, [blob, buildFile, inferredName, fileUrl, onFallbackUsed, onShared]);

    const onClickShare = useCallback(async () => {
        if (disabled || loading) return;

        try {
            const file = buildFile();

            if (canShareFile(file)) {
                const navAny = navigator as any;
                await navAny.share({
                    title: shareTitle || inferredName,
                    text: shareText || undefined,
                    files: [file],
                });
                onShared?.();
                return;
            }

            fallbackDownloadAndCopy();
        } catch (e) {
            onError?.(e);
            fallbackDownloadAndCopy();
        }
    }, [
        disabled,
        loading,
        buildFile,
        shareTitle,
        shareText,
        inferredName,
        onShared,
        onError,
        fallbackDownloadAndCopy,
    ]);

    const isDisabled = disabled || loading || !blob;

    return (
        <>
            {variant === 'default' ? (
                <button
                    type="button"
                    className={`close_modal_btn default primary d-flex align-items-center gap-2 p-ripple ${className ?? ''}`}
                    onClick={onClickShare}
                    disabled={isDisabled}
                    aria-busy={loading}
                >
                    <i className={loading ? "bi bi-arrow-repeat fx-spin" : "bi bi-share"} />
                    <span>{loading ? "Preparing…" : "Share"}</span>
                    <Ripple />
                </button>
            ) : variant === 'custom' ? (
                <button
                    type="button"
                    className={`document_nav_bar_action_btn p-ripple ${className ?? ''}`}
                    onClick={onClickShare}
                    disabled={isDisabled}
                    aria-busy={loading}
                >
                    <i className={loading ? "bi bi-arrow-repeat fx-spin" : "bi bi-share"} />
                    <Ripple />
                </button>
            ) : variant === 'icon' ? (
                <button
                    className="fxpv-iconbtn p-ripple"
                    onClick={onClickShare}
                    type="button"
                    aria-label="Share"
                    aria-busy={loading}
                    title="Share"
                    disabled={isDisabled}
                >
                    <i className={loading ? "bi bi-arrow-repeat fx-spin" : "bi bi-share"} />
                    <Ripple />
                </button>
            ) : null}

            {/* <button
                type="button"
                className={["fx-share-btn", isDisabled ? "is-disabled" : "", pressed ? "is-pressed" : "", className]
                    .filter(Boolean)
                    .join(" ")}
                onClick={onClickShare}
                disabled={isDisabled}
                aria-busy={loading}
                onMouseDown={() => setPressed(true)}
                onMouseUp={() => setPressed(false)}
                onMouseLeave={() => setPressed(false)}
            >
                <span className="fx-share-btn__icon" aria-hidden="true">
                    <i className={loading ? "bi bi-arrow-repeat fx-spin" : "bi bi-share"} />
                </span>

                <span className="fx-share-btn__label">{loading ? "Preparing…" : "Share"}</span>

                <span className="fx-share-btn__hint" aria-hidden="true">
                    <i className="bi bi-chevron-right" />
                </span>
            </button> */}
        </>
    );
};